import { Command, CommandGroupSource, CommandQuery } from "@omegagrid/commands";
import { html as oghtml } from "@omegagrid/core";
import { utils } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { CommandProvider } from "@reporting/rp-app-host";
import { Thread, File } from "@reporting/rp-types/etl";
import { css, html } from "lit";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { styleMap } from "lit/directives/style-map.js";

export type SearchResultCommand = Command & {
	props: {
		preview?: string;
	};
}

function highlightText(text: string, term: string): string {
	const m = (new RegExp(utils.escapeRegExp(term), 'gi'));
	if (m == null) return null;
	const matches: [number, number][] = [];
	let match: RegExpExecArray;
	while (match = m.exec(text)) {
		matches.push([match.index, match.index + term.length]);
	}
	return oghtml.highlightText(text, matches);
}

export class ETLSearchProvider implements CommandProvider {

	id = 'ETL';
	title = msg('ETL');
	placeholder = msg('Search ETL threads or files...');
	triggerCommand = 'e';
	lastQuery: CommandQuery;

	switches = [
		{key: 'threads', value: msg('Threads'), checked: true},
		{key: 'files', value: msg('Files'), checked: true},
	];

	getGroups = async (query?: CommandQuery): Promise<CommandGroupSource[]> => {
		this.lastQuery = query;
		if (!query?.filter) return [];

		const result = await rp.ms('etl').get<{threads: Thread[], files: File[]}>('/search', {
			q: query.filter,
			objects: query.switches
		});

		const groups: CommandGroupSource[] = [];

		if (result.threads?.length) {
			groups.push({
				id: 'threads',
				name: msg('Threads'),
				commands: result.threads.map(thread => ({
					id: `etl-thread-${thread.id_thread}`,
					name: thread.description,
					props: {
						preview: thread.command_preview
					},
					exec: () => {
						rp.host.open(`etl-thread-${thread.id_thread}`, {title: thread.description});
					}
				}))
			});
		}

		if (result.files?.length) {
			groups.push({
				id: 'files',
				name: msg('Files'),
				commands: result.files.map(file => ({
					id: `etl-file-${file.id_file}`,
					name: file.input_file,
					props: {
						preview: file.description
					},
					exec: () => {
						rp.host.open(`etl-file-${file.id_file}`, {title: file.input_file});
					}
				}))
			});
		}

		return groups;
	}

	getOptions = () => ({
		commandRenderer: (_div: HTMLDivElement, command: SearchResultCommand) => {
			const detailHTML = this.lastQuery?.filter && command.props?.preview
				? highlightText(command.props.preview, this.lastQuery.filter)
				: command.props?.preview;

			const nameHTML = command.name && this.lastQuery.filter
				? highlightText(command.name, this.lastQuery.filter)
				: command.name;

			return html`
				<div class="command" style="${styleMap({
					height: detailHTML ? null : '30px',
				})}">
					<div class="command-text">${unsafeHTML(nameHTML)}</div>
					${detailHTML ? html`<div class="command-detail">${unsafeHTML(detailHTML)}</div>` : null}
				</div>
			`;
		},
		itemHeight: 60,
		itemStyle: css`
			.item .command {
				display: flex;
				flex-direction: column;
				justify-content: center;
				height: 60px;
			}

			.item .command .command-text {
				flex: 0;
			}

			.item .command .command-detail {
				flex: 0 0 40px;
				font-size: 0.65em;
				overflow: hidden;
				text-overflow: ellipsis;
			}
		`
	});

}
